import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const token = params.get("token") || "";
  const [phase, setPhase] = useState(token ? "verifying" : "missing");
  const [message, setMessage] = useState("");
  const [credits, setCredits] = useState(0);
  const ranRef = useRef(false);

  useEffect(() => {
    if (!token || ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (data?.token) {
          try { localStorage.setItem("pf_token", data.token); } catch (_) { /* ignore */ }
          try { await refresh(); } catch (_) { /* ignore */ }
        }
        setCredits(Number(data?.user?.credits || 0));
        setMessage(data?.message || t("auth.verify_success"));
        setPhase("success");
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        setMessage(
          typeof detail === "string" && detail
            ? detail
            : status === 429
              ? t("auth.verify_too_many_attempts")
              : t("auth.verify_invalid_or_expired")
        );
        setPhase("failed");
      }
    })();
  }, [token, refresh, t]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {phase === "verifying" && (
        <div data-testid="verify-email-verifying">
          <Loader2 className="w-12 h-12 mx-auto text-blue-500 animate-spin mb-6" />
          <h1 className="text-2xl font-semibold mb-2">{t("auth.verify_verifying")}</h1>
        </div>
      )}
      {phase === "success" && (
        <div data-testid="verify-email-success">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-6" />
          <h1 className="text-3xl font-semibold tracking-tight mb-2">{t("auth.verify_email_verified")}</h1>
          <p className="text-zinc-400 mb-6">{message}</p>
          {credits > 0 && (
            <p className="text-zinc-400 mb-6">
              {t("auth.verify_you_now_have_prefix")}{" "}
              <span className="font-mono text-white" data-testid="verify-email-credits">{credits}</span>{" "}
              {t("auth.verify_you_now_have_suffix")}
            </p>
          )}
          <div className="flex gap-3 justify-center flex-wrap">
            <Button onClick={() => nav("/generate")} data-testid="verify-generate-btn"
                    className="bg-blue-600 hover:bg-blue-500 text-white">
              {t("auth.verify_generate_btn")}
            </Button>
            <Button onClick={() => nav("/dashboard")} variant="outline" data-testid="verify-dashboard-btn"
                    className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
              {t("auth.verify_dashboard_btn")}
            </Button>
          </div>
        </div>
      )}
      {(phase === "failed" || phase === "missing") && (
        <div data-testid="verify-email-failed">
          <AlertCircle className="w-14 h-14 mx-auto text-amber-400 mb-6" />
          <h1 className="text-2xl font-semibold mb-2">{t("auth.verify_failed_title")}</h1>
          <p className="text-zinc-500 mb-6">
            {phase === "missing"
              ? t("auth.verify_missing_token")
              : message}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/signup" data-testid="verify-resend-signup">
              <Button variant="outline" className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
                {t("auth.verify_signup_again")}
              </Button>
            </Link>
            <Link to="/login" data-testid="verify-back-login">
              <Button variant="outline" className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
                {t("auth.verify_back_to_login")}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
